// 2026-04-24: Trang NV xem tiền thưởng đã đạt + còn chờ chi.
// Yêu cầu L2 (NV cụ thể) — admin không vào được vì không có req.employee.
//
// Layout:
//   Header — back về /, tiêu đề "Tiền thưởng của bạn"
//   Hero card — tổng đã đạt · đã nhận · đang chờ chi (3 stat)
//   Section "Mốc đã đạt" — list từng milestone, status đã/chưa nhận
//   Section "Mốc còn lại" — danh sách bộ chưa đậu, kèm số tiền sẽ thưởng

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';

function fmtVnd(v) {
  if (v === null || v === undefined) return '0đ';
  return Number(v).toLocaleString('vi-VN') + 'đ';
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
}

export default function RewardsPage() {
  const user = useAuthStore(s => s.user);
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const r = await api.rewardsMe();
        if (!cancelled) setData(r);
      } catch (e) {
        if (e.status === 400 || e.status === 403) {
          toast.error('Chưa xác minh L2 — quay lại Trang chủ.');
          nav('/', { replace: true });
        } else {
          toast.error('Lỗi tải dữ liệu thưởng: ' + (e?.message || e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [nav]);

  return (
    <div className="min-h-full bg-gradient-to-br from-amber-50 to-emerald-50">
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        {/* Header */}
        <header className="mb-4 flex items-center gap-3">
          <Link
            to="/"
            className="text-sm px-3 py-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700"
          >
            ← Về trang chủ
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-amber-700">🎁 Tiền thưởng của bạn</h1>
            <p className="text-xs text-slate-500">
              Mỗi bộ NV đậu kiểm tra (≥ 90 điểm) sẽ được thưởng theo cấu hình của shop.
            </p>
          </div>
        </header>

        {loading && <div className="text-sm text-slate-500">Đang tải…</div>}

        {!loading && data && (
          <>
            {/* Hero card 3 stats */}
            <div className="bg-white rounded-2xl shadow border border-amber-200 p-5 mb-4">
              <div className="text-xs text-slate-500 mb-1">Tổng tiền đã đạt được</div>
              <div className="text-3xl font-bold text-amber-700">
                {fmtVnd(data.totals.total_achieved_vnd)}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="text-xs text-emerald-600">✅ Đã nhận</div>
                  <div className="text-lg font-bold text-emerald-700">
                    {fmtVnd(data.totals.total_paid_vnd)}
                  </div>
                  <div className="text-xs text-emerald-500 mt-0.5">
                    {data.totals.paid_count} mốc
                  </div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                  <div className="text-xs text-rose-600">⏳ Đang chờ chi</div>
                  <div className="text-lg font-bold text-rose-700">
                    {fmtVnd(data.totals.total_pending_vnd)}
                  </div>
                  <div className="text-xs text-rose-500 mt-0.5">
                    {data.totals.unpaid_count} mốc
                  </div>
                </div>
              </div>
            </div>

            {/* Mốc đã đạt */}
            <div className="bg-white rounded-2xl shadow border border-slate-200 p-5 mb-4">
              <h2 className="text-base font-semibold mb-3">🏆 Mốc bạn đã đạt</h2>
              {data.milestones.length === 0 ? (
                <div className="text-sm text-slate-400 italic">
                  Bạn chưa đậu mốc nào — hãy ôn tập + làm bài kiểm tra để mở khoá thưởng!
                </div>
              ) : (
                <div className="space-y-2">
                  {data.milestones.map((m) => (
                    <div
                      key={m.id}
                      className={`border rounded-lg p-3 ${
                        m.reward_paid === 1
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-amber-50 border-amber-200'
                      }`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="font-semibold text-slate-800">{m.bo_label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Đậu ngày {fmtDate(m.achieved_at)} · điểm {m.score}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-amber-700">
                            {fmtVnd(m.reward_amount_vnd)}
                          </div>
                          {m.reward_paid === 1 ? (
                            <div className="text-xs text-emerald-700">
                              ✅ Đã nhận {m.paid_at ? `(${fmtDate(m.paid_at)})` : ''}
                            </div>
                          ) : (
                            <div className="text-xs text-rose-600">⏳ Chờ chủ shop chi</div>
                          )}
                        </div>
                      </div>
                      {m.paid_note && (
                        <div className="text-xs text-slate-500 mt-2 italic">
                          📝 Ghi chú: {m.paid_note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mốc còn lại */}
            {data.next_milestones.length > 0 && (
              <div className="bg-white rounded-2xl shadow border border-slate-200 p-5">
                <h2 className="text-base font-semibold mb-3">🎯 Mốc bạn có thể mở khoá</h2>
                <p className="text-xs text-slate-500 mb-3">
                  Đậu kiểm tra (≥ 90 điểm) các bộ dưới để nhận thêm thưởng:
                </p>
                <div className="space-y-2">
                  {data.next_milestones.map((m) => (
                    <div
                      key={m.bo}
                      className="border border-dashed border-slate-300 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="font-medium text-slate-700">{m.bo_label}</div>
                      <div className="text-amber-700 font-semibold">
                        +{fmtVnd(m.reward_amount_vnd)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-center">
                  <Link
                    to="/exam"
                    className="inline-block px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
                  >
                    Đi làm kiểm tra ngay →
                  </Link>
                </div>
              </div>
            )}

            {data.next_milestones.length === 0 && data.milestones.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                <div className="text-2xl mb-1">🎉</div>
                <div className="font-semibold text-emerald-700">
                  Bạn đã đạt tất cả các mốc thưởng!
                </div>
                <div className="text-xs text-emerald-600 mt-1">
                  Tiếp tục giữ phong độ + giúp đỡ đồng nghiệp nhé.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
